import React, { lazy } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { Button } from 'react-bootstrap';
import Skeleton from 'wsm-skeleton';
import {
	useFlags,
	useLabels,
	usePrefs,
	useRequestData
} from 'ws-scripts/modules/common';
import DeferredComponent from '../ws-scripts/components/DeferredComponent';
import { setModalSettings } from '../actions/modalSettings';
import VehicleImage from '../components/VehicleImage';
import Callout from '../components/Callout';

const LazyVehicleImageCarousel = lazy(() =>
	import('../components/VehicleImageCarousel')
);

const MediaContainer = ({
	enableMediaCarousel,
	isTabbedLayout,
	vehicle,
	accountPhone
}) => {
	const flags = useFlags();
	const dispatch = useDispatch();
	const labels = useLabels();
	const { deviceType, widgetName, windowId } = useRequestData();
	const { showVideoModalButton, openVideoModalLabel } = usePrefs();

	const { videos = [], uuid, title, link } = vehicle;

	const carouselDeferMaxWait = flags['srp-deferred-carousel-max-wait'];

	const phone = accountPhone;

	let videoButton = null;
	if (showVideoModalButton === 'true' && videos.length > 0) {
		videoButton = (
			<Button
				bsSize={deviceType === 'DESKTOP' ? 'xsmall' : null}
				className={`open-media-modal-btn ${
					deviceType === 'MOBILE' ? 'text-nowrap' : ''
				}`}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Video button',
						result: 'Video modal displayed'
					});

					dispatch(
						setModalSettings({
							isModalOpen: true,
							videos,
							uuid,
							title,
							link,
							phone
						})
					);
				}}
			>
				<i className="ddc-icon ddc-icon-play2 mr-2" />
				<span className="text-link text-link-muted">
					{labels.get(openVideoModalLabel)}
				</span>
			</Button>
		);
	}

	const forceCarouselMatch = document.location.search.match(
		/_forceMediaCarousel=([a-z]*)/
	);
	const showCarousel =
		forceCarouselMatch !== null
			? forceCarouselMatch[1] === 'true'
			: enableMediaCarousel;

	const mediaSkeleton = (
		<Skeleton skeletonClasses="mb-4" aspectRatio="4by3" />
	);
	return (
		<div className="vehicle-card-media-container">
			{showCarousel && vehicle.images.length > 1 ? (
				<DeferredComponent
					maxWait={carouselDeferMaxWait}
					placeholder={mediaSkeleton}
				>
					<LazyVehicleImageCarousel
						images={vehicle.images}
						link={vehicle.link}
						uuid={vehicle.uuid}
						videoButton={videoButton}
					/>
				</DeferredComponent>
			) : (
				<VehicleImage
					image={vehicle.images[0]}
					imageIndex={0}
					enableImage
					link={vehicle.link}
					uuid={vehicle.uuid}
				/>
			)}
			{videoButton}
			{isTabbedLayout && (
				<ul
					className="list-unstyled d-flex flex-column vehicle-card-media align-items-center"
					data-location="vehicle-media"
				/>
			)}
			{!isTabbedLayout && (
				<Callout
					callouts={vehicle.callout}
					{...{
						isTabbedLayout
					}}
				/>
			)}
		</div>
	);
};

MediaContainer.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		images: PropTypes.arrayOf(PropTypes.object),
		uuid: PropTypes.string,
		link: PropTypes.string,
		title: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		phone: PropTypes.string,
		callout: PropTypes.arrayOf(PropTypes.object),
		videos: PropTypes.arrayOf(PropTypes.object)
	}).isRequired,
	isTabbedLayout: PropTypes.bool.isRequired,
	accountPhone: PropTypes.string.isRequired
};

export default MediaContainer;
